import { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Typography, Box, Tooltip, CircularProgress } from '@mui/material';

// comps
import GlobalInput from '../../components/global/input';
import FilterContainer from '../../components/layout/filterContainer';
import ScheduleGrid from '../../components/grid/scheduleGrid';
import ContentContainer from '../../components/layout/contentContainer';
import GlobalDropdown from '../../components/global/dropdown';
import GlobalButton from '../../components/global/button';

// icons
import { ReactComponent as CommentIcon } from '../../assets/icons/comment-icon.svg';
import { ReactComponent as DirectArrowIcon } from '../../assets/icons/arrow-triangle-icon.svg';
import { ReactComponent as FutureArrowIcon } from '../../assets/icons/arrow-media-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';

// API
import BuildingApi from '../../api/building.api';
import MessagesApi from '../../api/messages.api';
import RoomApi from '../../api/room.api';

// constants
import { colors } from '../../theme';
import { MOMENT_DATE_FORMAT } from '../../constants/date_format';
import { useAppSelector } from '../../hooks/hooks';
import { accountTypes, CENTRAL_TIMETABLER_VIEW_TYPE, FACILITIES_VIEW_TYPE } from '../../constants';

const DailyGrid = (props: any) => {
    const _buildingApi = new BuildingApi();
    const _messagesApi = new MessagesApi();
    const _roomApi = new RoomApi();

    const appState = useAppSelector((state) => state.appState);
    const user = useAppSelector((state) => state.user);

    const { viewType } = appState;

    //FIXME: centralize this logic and all others like this in a function
    const isCentralTimetablerView = user.accountType === accountTypes.centralTimetabler || appState.viewType === CENTRAL_TIMETABLER_VIEW_TYPE;

    const [state, setState] = useState({
        date: moment().format(MOMENT_DATE_FORMAT),
        data: [],
        buildingSchedule: [],
        specialMessage: null,
        buildings: [],
        floors: [],
    });

    const [isLoading, setIsLoading] = useState(false);
    const [buildings, setBuildings] = useState([]);

    const [selected, setSelected] = useState({
        building: null,
        floor: null,
    });

    //TODO: error handling (PS)
    const loadBuildingsData = async (campusId?: number) => {
        setIsLoading(true);
        const buildings = await _buildingApi.GetAllBuildings(isCentralTimetablerView || viewType === FACILITIES_VIEW_TYPE ? campusId : null);
        let selectedBuilding = null;

        // //Auto select if there is only one building. The building dropdown will be hidden
        if (buildings.length === 1) {
            selectedBuilding = buildings[0];
        }
        setState({
            ...state,
            data: [],
        });

        setBuildings(buildings);

        setSelected({
            ...selected,
            building: selectedBuilding,
        });
    };

    const loadBuildingSchedule = async (date: Date) => {
        setIsLoading(true);
        let data = [];
        let buildingSchedule = []

        if (selected?.building) {
            data = buildingSchedule = await _buildingApi.GetBuildingSchedule(selected.building.id, date);
        }

        if (selected?.floor) {
            buildingSchedule = buildingSchedule.filter((s) => s.roomNumber[0] === selected.floor.name);
        }

        const specialMessage = await _messagesApi.GetMessageByDate(date);

        setState((prevState) => ({
            ...prevState,
            data,
            buildingSchedule,
            specialMessage,
        }));
    };

    useEffect(() => {
        loadBuildingsData(appState.campus?.id)
            .catch((error) => {
                console.log(error);
                toast.error('Error loading buildings');
            })
            .finally(() => setIsLoading(false));
    }, [appState.campus]);

    useEffect(() => {
        if (state.date || selected.building) {
            loadBuildingSchedule(moment(state.date).toDate())
                .catch((error) => {
                    console.log(error);
                    toast.error('Error loading data');
                })
                .finally(() => setIsLoading(false));
        }
    }, [selected.building, state.date]);

    useEffect(() => {
        if (selected.floor) {
            const buildingSchedule = state.data.filter((s) => s.roomNumber.charAt(0) === selected.floor.name);

            setState((prevState) => ({
                ...prevState,
                buildingSchedule,
            }));
        }
    }, [selected.floor]);

    const handleDateChange = (name: string, value: any) => {
        setState((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleClick = (length: any, type: any) => {
        if (type === 'next') {
            setState({
                ...state,
                date: moment(state.date)?.add(1, length).format(MOMENT_DATE_FORMAT),
            });
        } else if (type === 'prev') {
            setState({
                ...state,
                date: moment(state.date)?.subtract(1, length).format(MOMENT_DATE_FORMAT),
            });
        }
    };

    const handleDateToggle = (length: any, type: any, event: any) => {
        if (event.detail >= 2) {
            setTimeout(() => {
                handleClick(length, type);
            }, 350);
        } else handleClick(length, type);
    };

    const handleBuildingChange = async (name: any, value: any) => {
        const rooms = await _roomApi.GetRooms(value.id);
        const floors = rooms.reduce((acc, room) => {
            const floor = room.number.charAt(0);

            if (!acc.some((f) => f.name === floor)) {
                acc.push({
                    id: acc.length + 1, // assign id as index + 1
                    name: floor,
                });
            }

            return acc;
        }, []);

        setSelected({ ...selected, building: value, floor: null });
        setState({ ...state, floors });
    };

    const handleFloorChange = async (name: any, value: any) => {
        setSelected((prevState) => ({
            ...prevState,
            floor: value,
        }));
    };

    const handleClearAll = () => {

        setSelected((prevState) => ({
            ...prevState,
            building: null,
            floor: null,
        }));

        setState((prevState) => ({
            ...prevState,
            data: [],
        }));

        setIsLoading(false);
    };

    const multipleBuildings = buildings?.length > 1;

    return (
        <>
            <FilterContainer sx={{ justifyContent: 'space-between' }}>
                <FlexBox>
                    <GlobalInput label="date" placeholder="Start Date" type="date" name="date" value={state.date} onChange={handleDateChange} sx={{ mr: '10px' }} />

                    {/* No need to show the building dropdown if there is only one building */}
                    {multipleBuildings && (
                        <GlobalDropdown
                            isSearch
                            label="Building"
                            value={selected.building}
                            data={buildings}
                            placeholder="Select..."
                            name="name"
                            field="id"
                            dataItemKey={'building'}
                            onChange={handleBuildingChange}
                            sx={{ mr: '10px' }}
                        />
                    )}
                    {multipleBuildings && (
                        <GlobalDropdown
                            label="Floor (Optional)"
                            value={selected.floor}
                            data={state.floors}
                            placeholder="Select..."
                            name="name"
                            field="id"
                            dataItemKey={'floor'}
                            onChange={handleFloorChange}
                            sx={{ mr: '10px' }}
                        />
                    )}
                </FlexBox>
                {multipleBuildings && selected.building && (
                    <Box sx={{ width: '10%' }}>
                        <GlobalButton variant="round" text="clear all" fullWidth secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} />
                    </Box>
                )}
            </FilterContainer>
            <ContentContainer>
                <FlexBox sx={{ justifyContent: 'space-between', height: '40px', alignItems: 'flex-start' }}>
                    <FlexBox sx={{ alignItems: 'center' }}>
                        <Typography variant="h4" color={colors.blue.main}>
                            Daily Course &amp; Event Schedule
                        </Typography>
                        {state.specialMessage && (
                            <InfoBox>
                                <CommentIcon />
                                <Typography variant="body2">{state.specialMessage.message}</Typography>
                            </InfoBox>
                        )}
                    </FlexBox>
                    {state.buildingSchedule?.length > 0 && (
                        <DateToggle>
                            <Tooltip title="Load previous week" arrow>
                                <FutureArrowIcon onClick={(e) => handleDateToggle('w', 'prev', e)} />
                            </Tooltip>
                            <span onClick={(e) => handleDateToggle('d', 'prev', e)}>
                                <DirectArrowIcon style={{ transform: 'rotate(180deg)' }} />
                                <Typography variant="body2" color={colors.grey.medium}>
                                    Previous
                                </Typography>
                            </span>
                            <Date variant="body1" color={colors.blue.main}>
                                {moment(state.date)?.format('ddd, MMM DD, YYYY')}
                            </Date>
                            <span onClick={(e) => handleDateToggle('d', 'next', e)}>
                                <Typography variant="body2" color={colors.grey.medium}>
                                    Next
                                </Typography>
                                <DirectArrowIcon />
                            </span>
                            <Tooltip title="Load next week" arrow>
                                <FutureArrowIcon style={{ transform: 'rotate(180deg)' }} onClick={(e) => handleDateToggle('w', 'next', e)} />
                            </Tooltip>
                        </DateToggle>
                    )}
                </FlexBox>
                {state.buildingSchedule?.length === 0 ? (
                    isLoading ? (
                        <FlexBox sx={{ justifyContent: 'center', height: '100%', alignItems: 'center' }}>
                            <CircularProgress size={100} />{' '}
                        </FlexBox>
                    ) : (
                        <FlexBox sx={{ justifyContent: 'center', height: '100%', alignItems: 'center', flexDirection: 'column' }}>
                            <CalendarIcon />
                            <Typography sx={{ color: colors.blue.main, mt: '1em' }}>
                                {selected.building ? `No data found.` : `Please select a building to generate a grid of corresponding rooms.`}
                            </Typography>
                        </FlexBox>
                    )
                ) : (
                    <FlexBox sx={{ justifyContent: 'center', maxHeight: '92%', overflowY: 'scroll', p: '1px', flexWrap: 'wrap' }}>
                        {state.buildingSchedule.map((o, i) => {
                            return <ScheduleGrid key={i} {...o} />;
                        })}
                    </FlexBox>
                )}
            </ContentContainer>
        </>
    );
};

export default DailyGrid;

const FlexBox = styled(Box)`
    && {
        display: flex;
    }
`;

const InfoBox = styled.div`
    background-color: ${colors.green.light};
    color: ${colors.green.main};
    display: flex;
    align-items: center;
    margin-left: 15px;
    padding: 2px 5px;
    border-radius: 0px 5px 5px 0px;
    border-left: 3px solid ${colors.green.main};

    svg {
        width: 20px;
        height: auto;
        margin-right: 3px;
        path {
            fill: ${colors.green.main};
        }
    }
`;

const Date = styled(Typography)`
    && {
        font-weight: 600;
        margin: 0px 10px;
        width: 122px;
        display: flex;
        justify-content: center;
    }
`;

const DateToggle = styled.div`
    display: flex;
    align-items: center;

    & > * {
        display: flex;
        align-items: center;
    }

    svg {
        cursor: pointer;
        width: auto;
        height: 18px;
        path {
            &:hover {
                fill: ${colors.blue.main};
            }
            fill: ${colors.grey.medium};
        }
    }

    span:hover {
        cursor: pointer;
        p {
            color: ${colors.blue.main};
        }
        svg {
            path {
                fill: ${colors.blue.main};
            }
        }
    }
`;
